import { useState, useEffect } from "react";
import {
  getFairwayDataLoadingState,
  startLoadingFairwayData,
} from "./fairwayRouting";

type FairwayDataLoadingState = {
  isLoading: boolean;
  isLoaded: boolean;
  error: Error | null;
};

const POLL_INTERVAL_MS = 250;

const listeners = new Set<(state: FairwayDataLoadingState) => void>();
let pollIntervalId: number | null = null;
let loadingStarted = false;
let cachedState: FairwayDataLoadingState = getFairwayDataLoadingState();

function hasStateChanged(
  prev: FairwayDataLoadingState,
  next: FairwayDataLoadingState,
): boolean {
  return (
    prev.isLoading !== next.isLoading ||
    prev.isLoaded !== next.isLoaded ||
    prev.error !== next.error
  );
}

function publishState(nextState: FairwayDataLoadingState): void {
  cachedState = nextState;
  listeners.forEach((listener) => listener(nextState));
}

function pollStateAndPublishIfChanged(): void {
  const nextState = getFairwayDataLoadingState();
  if (!hasStateChanged(cachedState, nextState)) return;
  publishState(nextState);
}

function ensurePolling(): void {
  if (pollIntervalId !== null || typeof window === "undefined") return;
  pollIntervalId = window.setInterval(pollStateAndPublishIfChanged, POLL_INTERVAL_MS);
}

function stopPollingIfUnused(): void {
  if (listeners.size > 0 || pollIntervalId === null) return;
  window.clearInterval(pollIntervalId);
  pollIntervalId = null;
}

/**
 * React hook to track fairway data loading state.
 * Automatically starts loading on first use if not already started.
 */
export function useFairwayDataLoading(): {
  isLoading: boolean;
  isLoaded: boolean;
  error: Error | null;
} {
  const [state, setState] = useState(() => cachedState);

  useEffect(() => {
    if (!loadingStarted) {
      startLoadingFairwayData();
      loadingStarted = true;
      cachedState = getFairwayDataLoadingState();
    }

    const handleStateChange = (nextState: FairwayDataLoadingState) => {
      setState((prev) => (hasStateChanged(prev, nextState) ? nextState : prev));
    };

    listeners.add(handleStateChange);
    setState((prev) => (hasStateChanged(prev, cachedState) ? cachedState : prev));
    ensurePolling();

    return () => {
      listeners.delete(handleStateChange);
      stopPollingIfUnused();
    };
  }, []);

  return state;
}
